import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';

import { api } from '@/utils/api';
import { applyOptimisticReorder } from '~/components/TemplateBlocksSection/templateBlocksLogic';

export const useCadenceTemplateDeleteMutation = () => {
  const utils = api.useUtils();
  return api.cadenceTemplate.delete.useMutation({
    onMutate: ({ cadenceTemplateId }) => {
      // utils.user.me.setData(undefined, (data) => {
      //   if (data && data.userTeam && data.userTeam.lastUsedCadenceTemplateId == cadenceTemplateId) {
      //     return {
      //       ...data,
      //       userTeam: { ...data.userTeam, lastUsedCadenceTemplateId: null },
      //     };
      //   }
      //   return data;
      // });
      utils.cadenceTemplate.idV2.setData({ cadenceTemplateId }, () => {
        return undefined;
      });
    },
    onSuccess: () => {
      void utils.cadenceTemplate.paginated.invalidate();
    },
    onError: () => {
      void utils.cadenceTemplate.idV2.invalidate();
      void utils.user.me.invalidate();
    },
  });
};

export const useCadenceTemplateUpdateMutation = () => {
  const utils = api.useUtils();

  return api.cadenceTemplate.update.useMutation({
    onSuccess: async () => {
      // The templates list (paginated) shows the template name/metadata too.
      await Promise.all([
        utils.cadenceTemplate.idV2.invalidate(),
        utils.cadenceTemplate.paginated.invalidate(),
      ]);
    },
  });
};

export const useCadenceTemplateChangeOwnerMutation = () => {
  const utils = api.useUtils();

  return api.cadenceTemplate.changeOwner.useMutation({
    onSuccess: async () => {
      // The templates list (paginated) shows the owner name/avatar.
      await Promise.all([
        utils.cadenceTemplate.idV2.invalidate(),
        utils.cadenceTemplate.paginated.invalidate(),
      ]);
    },
  });
};

export const useCadenceUpdateTemplateStepMutation = () => {
  const utils = api.useUtils();
  const queryClient = useQueryClient();
  // Resolved once at hook init — `getQueryKey(api.cadenceTemplate.idV2)`
  // returns the operation prefix (no input filtering), so
  // `setQueriesData` updates EVERY cached idV2 entry that contains the
  // step, regardless of which `cadenceTemplateId` input was used.
  // Mirrors the campaign-side `useCadenceUpdateStepMutation` patching
  // `contact.byId` so body/subject edits land instantly without
  // waiting for the server round trip + refetch.
  const idV2QueryKey = getQueryKey(api.cadenceTemplate.idV2);

  return api.cadenceTemplate.updateStep.useMutation({
    onMutate: async ({ stepId, data }) => {
      await queryClient.cancelQueries({ queryKey: idV2QueryKey });
      const snapshots = queryClient.getQueriesData({ queryKey: idV2QueryKey });

      queryClient.setQueriesData({ queryKey: idV2QueryKey }, (old: unknown) => {
        if (old == null || typeof old !== 'object') return old;
        const oldRec = old as {
          cadenceTemplate?: { steps?: unknown[] | null } | null;
        };
        const steps = oldRec.cadenceTemplate?.steps;
        if (!Array.isArray(steps)) return old;
        let touched = false;
        const updatedSteps = steps.map((s) => {
          const step = s as Record<string, unknown> & { id: string };
          if (step.id !== stepId) return s;
          touched = true;
          // Patch every server-accepted field present in the payload.
          // Empty strings (cleared body/subject) are real saves —
          // preserve them. Only skip when the field is `undefined`.
          const patch: Record<string, unknown> = {};
          if (data.body2 !== undefined) patch.body2 = data.body2;
          if (data.subject !== undefined) patch.subject = data.subject;
          if (data.cc !== undefined) patch.cc = data.cc;
          if (data.bcc !== undefined) patch.bcc = data.bcc;
          if (data.postsToLike !== undefined) patch.postsToLike = data.postsToLike;
          if (data.onlyOpenLink !== undefined) patch.onlyOpenLink = data.onlyOpenLink;
          if (data.type !== undefined) patch.type = data.type;
          if (data.delay !== undefined) patch.delay = data.delay;
          if (data.delayAlignWeek !== undefined) patch.delayAlignWeek = data.delayAlignWeek;
          if (data.siftConditions !== undefined) patch.siftConditions = data.siftConditions;
          if (data.isPersonalized !== undefined) patch.isPersonalized = data.isPersonalized;
          if (data.personalizationPrompt !== undefined) {
            patch.personalizationPrompt = data.personalizationPrompt;
          }
          if (data.replyToPrevious !== undefined) patch.replyToPrevious = data.replyToPrevious;
          return { ...step, ...patch };
        });
        if (!touched) return old;
        return {
          ...oldRec,
          cadenceTemplate: { ...oldRec.cadenceTemplate, steps: updatedSteps },
        };
      });

      return { snapshots };
    },
    // ROLLBACK on failure: server validation (e.g. empty body for a
    // message step) throws and we put the prior content back instantly.
    onError: (_err, _vars, ctx) => {
      const snapshots = (ctx as { snapshots?: ReturnType<typeof queryClient.getQueriesData> })
        ?.snapshots;
      if (snapshots) {
        for (const [key, data] of snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
      void utils.cadenceTemplate.idV2.invalidate();
    },
    onSuccess: () => {
      void utils.cadenceTemplate.idV2.invalidate();
    },
  });
};

export const useCadenceTemplateDeleteStepMutation = () => {
  const utils = api.useUtils();
  return api.cadenceTemplate.deleteStep.useMutation({
    onMutate: () => {
      // utils.cadenceTemplate.idV2.setData({ cadenceTemplateId }, (data) => {
      //   if (data?.cadenceTemplate) {
      //     const cadenceTemplate = data.cadenceTemplate;
      //     const steps = omit(cadenceTemplate.steps, [stepId]);
      //     const orderedStepIds = without(data.cadenceTemplate.orderedStepIds, stepId);
      //     return {
      //       ...data,
      //       cadenceTemplate: {
      //         ...data.cadenceTemplate,
      //         orderedStepIds,
      //         steps,
      //       },
      //     };
      //   }
      // });
    },

    onSuccess: (template) => {
      void utils.cadenceTemplate.idV2.invalidate({
        cadenceTemplateId: template.cadenceTemplate.id,
      });
    },
  });
};

export const useContactCreateCadenceMutation = () => {
  const utils = api.useUtils();
  return api.cadenceTemplate.startFromContact.useMutation({
    onSuccess: async () => {
      void utils.user.me.invalidate();
      await Promise.all([
        utils.cadence.cadences2.invalidate(),
        // Campaigns section list (CampaignListView/CampaignDetailView) on the
        // contact-detail panel — direct (non-staging) enrollment lands here.
        utils.cadence.forContact.invalidate(),
        utils.cadence.fetchPaginatedExecutables.invalidate(),
        utils.cadenceTemplate.idV2.invalidate(),
        utils.cadenceTemplate.candidateListForContact.invalidate(),
        utils.contact.byId.invalidate(),
        // Staging-candidate badges ("researching" etc.) on the contact-detail
        // panel come from this bundle — prefilter/personalized enrollment lands
        // here. Without this the new campaign only shows after a hard refresh.
        utils.contactDetailBootstrap.secondaryForContact.invalidate(),
        utils.contact.search.invalidate(),
        utils.history.contact.invalidate(),
      ]);
    },
  });
};

/**
 * Approve a personalized candidate, promoting it to an active cadence.
 * Mirrors the invalidation set of `useContactCreateCadenceMutation` so
 * the contact-detail badge swaps from "needs review" to the running
 * cadence pill without a hard refresh.
 */
export const useCandidateApproveMutation = ({
  cadenceTemplateId,
}: {
  cadenceTemplateId: string;
}) => {
  const utils = api.useUtils();
  return api.cadenceTemplate.candidateApprove.useMutation({
    onSuccess: async () => {
      void utils.user.me.invalidate();
      await Promise.all([
        utils.cadenceTemplate.candidateCounts.invalidate({ cadenceTemplateId }),
        utils.cadenceTemplate.candidateList.invalidate({ cadenceTemplateId }),
        utils.cadenceTemplate.candidateListForContact.invalidate(),
        utils.cadence.cadences2.invalidate(),
        // Promotion swaps the staging badge for a running cadence pill: refresh
        // both the Campaigns-section list and the staging-candidate bundle on
        // the contact-detail panel so neither lags behind a hard refresh.
        utils.cadence.forContact.invalidate(),
        utils.contactDetailBootstrap.secondaryForContact.invalidate(),
        utils.cadence.fetchPaginatedExecutables.invalidate(),
        utils.cadenceTemplate.idV2.invalidate(),
        utils.contact.byId.invalidate(),
        utils.contact.search.invalidate(),
        utils.history.contact.invalidate(),
      ]);
    },
  });
};

/**
 * Reject a personalized candidate. Lighter invalidation than approve:
 * reject never creates a cadence row, so only the candidate counts /
 * lists need to refresh.
 */
export const useCandidateRejectMutation = ({
  cadenceTemplateId,
}: {
  cadenceTemplateId: string;
}) => {
  const utils = api.useUtils();
  return api.cadenceTemplate.candidateReject.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.cadenceTemplate.candidateCounts.invalidate({ cadenceTemplateId }),
        utils.cadenceTemplate.candidateList.invalidate({ cadenceTemplateId }),
        utils.cadenceTemplate.candidateListForContact.invalidate(),
      ]);
    },
  });
};

/**
 * Update a candidate draft's body / subject. Intentionally does NOT
 * invalidate `candidateDrafts` on success — that refetch would replace
 * the editor's `value` mid-typing, jumping the cursor to the end and
 * dropping keystrokes that landed after the mutation fired. The server
 * always echoes the value we just sent, so the cached copy stays
 * accurate without a round-trip.
 */
export const useCandidateDraftUpdateMutation = () => {
  return api.cadenceTemplate.candidateDraftUpdate.useMutation();
};

/**
 * Switch the active variation (V1 / V2 / V3) on a candidate draft. The
 * server swaps in the variation's `body{N}` / `subject{N}` for that
 * step, so this path *does* invalidate `candidateDrafts` so the editor
 * picks up the new values.
 */
export const useCandidateDraftSwitchVariationMutation = ({
  cadenceTemplateId,
  candidateId,
}: {
  cadenceTemplateId: string;
  candidateId: number;
}) => {
  const utils = api.useUtils();
  return api.cadenceTemplate.candidateDraftUpdate.useMutation({
    onSuccess: () => {
      void utils.cadenceTemplate.candidateDrafts.invalidate({ cadenceTemplateId, candidateId });
    },
  });
};

export const useCadenceTemplateReorderStepsMutation = () => {
  const utils = api.useUtils();
  const queryClient = useQueryClient();
  // Resolved once at hook init — `getQueryKey(api.cadenceTemplate.idV2)`
  // returns the operation prefix so `setQueriesData` updates EVERY
  // cached idV2 entry. Mirrors the optimistic patch pattern from
  // `useCadenceUpdateTemplateStepMutation` and `useCadenceUpdateStepMutation`.
  const idV2QueryKey = getQueryKey(api.cadenceTemplate.idV2);

  return api.cadenceTemplate.reorderSteps.useMutation({
    // OPTIMISTIC UPDATE
    // Without this, dragging a card produces a flicker:
    //   1. dnd-kit drops the card visually
    //   2. mutation fires; cache hasn't changed → card snaps back
    //   3. mutation succeeds → invalidate → refetch → new order shows
    //
    // The patch rewrites each delay step's `parent_id` to mirror what
    // the server's `cadenceTemplateReorderDelayGroups` does — for
    // each id in orderedDelayIds, set parent_id = previousId (or null
    // for the first). Other steps (wfc, runnables) keep their
    // existing parent_id; the graph rebuilds correctly on the next
    // render.
    onMutate: async ({ orderedDelayIds }) => {
      await queryClient.cancelQueries({ queryKey: idV2QueryKey });
      const snapshots = queryClient.getQueriesData({ queryKey: idV2QueryKey });

      queryClient.setQueriesData({ queryKey: idV2QueryKey }, (old: unknown) => {
        if (old == null || typeof old !== 'object') return old;
        const oldRec = old as {
          cadenceTemplate?: { steps?: unknown[] | null } | null;
        };
        const steps = oldRec.cadenceTemplate?.steps;
        if (!Array.isArray(steps)) return old;
        const updatedSteps = applyOptimisticReorder(
          steps as { id: string; parent_id: string | null }[],
          orderedDelayIds,
        );
        return {
          ...oldRec,
          cadenceTemplate: { ...oldRec.cadenceTemplate, steps: updatedSteps },
        };
      });

      return { snapshots };
    },
    // ROLLBACK on failure: server validation could reject the order
    // (e.g. a wfc id snuck in). Restore the prior data so the UI
    // bounces back to the real state.
    onError: (_err, _vars, ctx) => {
      const snapshots = (ctx as { snapshots?: ReturnType<typeof queryClient.getQueriesData> })
        ?.snapshots;
      if (snapshots) {
        for (const [key, data] of snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
      void utils.cadenceTemplate.idV2.invalidate();
    },
    onSuccess: async () => {
      await Promise.all([utils.cadenceTemplate.idV2.invalidate()]);
    },
  });
};

export const useCadenceTemplateCreateStepMutation = () => {
  const utils = api.useUtils();
  return api.cadenceTemplate.createStepV2.useMutation({
    onMutate: () => {
      // utils.cadenceTemplate.idV2.setData({ cadenceTemplateId }, (res) => {
      //   if (res?.cadenceTemplate) {
      //     const cadenceTemplate = res.cadenceTemplate;
      //     const { beforeStepId, afterStepId, ...step } = data;
      //     const newStepId = 'newStep' + Date.now();
      //     const beforeStep = beforeStepId ? cadenceTemplate.steps?.[beforeStepId] : undefined;
      //     const afterStep = afterStepId ? cadenceTemplate.steps?.[afterStepId] : undefined;
      //     const newSteps = Object.assign({}, cadenceTemplate.steps, {
      //       [newStepId]: {
      //         id: newStepId,
      //         ...step,
      //         weight: calcuateStepWeight(afterStep?.weight, beforeStep?.weight),
      //       },
      //     });
      //     const orderedStepIds = sortBy(Object.values(newSteps || {}), (s) => s.weight).map(
      //       (s) => s.id,
      //     );
      //     return {
      //       ...res,
      //       cadenceTemplate: {
      //         ...res.cadenceTemplate,
      //         orderedStepIds,
      //         steps: newSteps,
      //       },
      //     };
      //   }
      // });
    },
    onSuccess: (data) => {
      void utils.cadenceTemplate.idV2.invalidate({ cadenceTemplateId: data.cadenceTemplate.id });
      // utils.cadenceTemplate.idV2.setData({ cadenceTemplateId: data.cadenceTemplate.id }, () => {
      //   return { cadenceTemplate: data.cadenceTemplate, team: data.team };
      // });
    },
    onError: () => {
      void utils.cadenceTemplate.idV2.invalidate();
    },
  });
};

export const useCadenceTemplateCreateMutation = () => {
  const utils = api.useUtils();
  return api.cadenceTemplate.create.useMutation({
    onSuccess: () => {
      // utils.cadenceTemplate.idV2.setData({ cadenceTemplateId: data.cadenceTemplate.id }, () => {
      //   return { cadenceTemplate: data.cadenceTemplate, team: data.team };
      // });
      void utils.cadenceTemplate.paginated.invalidate();
    },
  });
};


// const calcuateStepWeight = (
//   afterStepWeight: number | undefined,
//   beforeStepWeight: number | undefined,
// ) => {
//   if (afterStepWeight != null && beforeStepWeight != null) {
//     return (beforeStepWeight + afterStepWeight) / 2;
//   } else if (afterStepWeight != null && beforeStepWeight == null) {
//     return afterStepWeight + 1;
//   } else if (afterStepWeight == null && beforeStepWeight != null) {
//     return beforeStepWeight / 2;
//   } else {
//     return 1;
//   }
// };

export const useCadenceTemplateCopyMutation = () => {
  const utils = api.useUtils();
  return api.cadenceTemplate.copy.useMutation({
    onSuccess: () => {
      void utils.cadenceTemplate.paginated.invalidate();
    },
  });
};

export const useCadenceTemplateArchiveMutation = () => {
  const utils = api.useUtils();
  return api.cadenceTemplate.archive.useMutation({
    onSuccess: () => {
      void utils.cadenceTemplate.paginated.invalidate();
      void utils.cadenceTemplate.idV2.invalidate();
    },
  });
};

export const useSuperAdminCopyTemplateToMyTeamMutation = () => {
  return api.superAdmin.copyTemplateToMyTeam.useMutation();
};

export const useCadenceTemplateAddTagMutation = () => {
  const utils = api.useUtils();

  return api.cadenceTemplate.addTag.useMutation({
    onSuccess: async (data) => {
      await Promise.all([utils.cadenceTemplate.idV2.invalidate({ cadenceTemplateId: data.cadenceTemplateId })]);
    },
  });
};

export const useCadenceTemplateRemoveTagMutation = () => {
  const utils = api.useUtils();

  return api.cadenceTemplate.removeTag.useMutation({
    onSuccess: async (data) => {
      await Promise.all([utils.cadenceTemplate.idV2.invalidate({ cadenceTemplateId: data.cadenceTemplateId })]);
    },
  });
};

export const useCadenceTemplateSetPriorityMutation = () => {
  const utils = api.useUtils();
  return api.cadenceTemplate.setPriority.useMutation({
    onSuccess: async (data) => {
      await Promise.all([utils.cadenceTemplate.idV2.invalidate({ cadenceTemplateId: data.cadenceTemplateId })]);
    },
  });
};

export const useCadenceTemplateAddStepsFromCsvMutation = () => {
  const utils = api.useUtils();
  return api.cadenceTemplate.addStepsFromCsv.useMutation({
    onSuccess: async () => {
      await Promise.all([utils.cadenceTemplate.idV2.invalidate()]);
    },
    onError: () => {
      void utils.cadenceTemplate.idV2.invalidate();
    },
  });
};
